// Где кабинет держит токен. SMY-175, SMY-341
//
// Отдельным модулем, а не внутри session.tsx, ради проверки: кабинет проверяется
// только файлами `.ts` (`pnpm test` не запускает `.tsx`), а здесь живёт правило,
// за нарушение которого человек платит деньгами, — `settle` и `keepSignedIn`.
// Сессия только зовёт `settle` с ответом `/v1/me`.

import type { TokenStore } from '@smyslika/api-client'

const TOKEN_KEY = 'smyslika.token'
const GUEST_KEY = 'smyslika.guest'

/** Хранилища браузера. Функциями: в приватном режиме бросает уже само обращение. */
export interface BrowserStorages {
  local: () => Storage
  session: () => Storage
}

export interface BrowserTokens {
  /** Хранилище для клиента API. */
  store: TokenStore
  /** Положить только что заведённого гостя во вкладочное хранилище. */
  keepAsGuest: (token: string) => void
  /**
   * Переложить токен признанного аккаунта из вкладочного хранилища в постоянное.
   * Ничего не делает, если токен уже там или токена нет.
   */
  keepSignedIn: () => void
  /**
   * Сервер ответил, кто это (`/v1/me`): не гость — токен в постоянное хранилище,
   * гость — остаётся во вкладке. Решение здесь, а не в сессии, ради проверки:
   * `.tsx` раннер не запускает, а ошибка в обе стороны стоит дорого — гость,
   * уехавший в `localStorage`, нарушает обещание «закроете вкладку — работы не
   * будет» (SMY-175), признанный аккаунт, оставшийся во вкладке, — «заплатил, а
   * видит бесплатный тариф» (SMY-341).
   */
  settle: (me: { signup?: unknown }) => void
  /**
   * Был ли гостевым токен, стёртый последним. SMY-357
   *
   * Клиент стирает отвергнутый токен ДО вызова `onUnauthorized`, и к тому
   * моменту ключа гостя в хранилище уже нет — спросить «был ли это гость»
   * можно только у того, кто стирал. Гостю войти обратно нечем, почты у него
   * нет, и сессия заводит ему нового гостя; аккаунту — экран входа.
   */
  lostGuest: () => boolean
}

/**
 * ДВА ХРАНИЛИЩА, И ЭТО НЕСУЩЕЕ РЕШЕНИЕ. SMY-175
 *
 * Признанный аккаунт живёт в `localStorage` — он должен пережить закрытие
 * вкладки. Гость живёт в `sessionStorage` и умирает вместе с ней, и это
 * ровно то, что мы обещаем на экране: не зарегистрировался — к работе не
 * вернёшься. Умирает при этом ДОСТУП, а не данные: учётная запись гостя лежит
 * на сервере до уборки брошенных гостей (п. 5.3 и 8.2 политики с редакции от
 * 21.09.2026; до неё здесь стояло «не сохранили», и так же врала политика).
 *
 * Обещание, не подкреплённое хранилищем, было бы обманом наоборот: человек
 * ушёл, полагая, что следов не осталось, а назавтра открыл вкладку и увидел
 * свою переписку. Поэтому обещание и хранилище заведены одним решением.
 *
 * ⚠️ ЧТЕНИЕ — СНАЧАЛА `localStorage`. Порядок несущий: гость, который вошёл в
 * существующий аккаунт, получает новый токен в `localStorage`, а старый
 * гостевой ключ снимается тем же движением. Обратный порядок оставил бы
 * человека на гостевой сессии после входа — с половиной прав в собственном
 * аккаунте, и заметил бы он это только по лимитам.
 *
 * ⚠️ НОВОГО ТОКЕНА НЕ БЫВАЕТ У ГОСТЯ, ПОДТВЕРДИВШЕГО СВОБОДНУЮ ПОЧТУ (21.09.2026,
 * SMY-341). Почта ложится в ту же строку, и сервер оставляет прежнюю сессию —
 * `set` не зовётся, и токен уже признанного аккаунта оставался в
 * `sessionStorage`. Закрыл вкладку — вышел из только что заведённого аккаунта.
 * А страница оплаты открывается новой вкладкой, и возврат с неё завёл бы нового
 * гостя: человек заплатил, а видит бесплатный тариф. Поэтому `keepSignedIn`, и
 * зовёт его сессия, как только сервер сказал «уже не гость» (`session.tsx`).
 *
 * Оба хранилища кидают в приватном режиме и при запрете сайтовых данных.
 * Падать из-за этого нельзя: человек просто окажется не вошедшим.
 */
export function createBrowserTokens(storages: BrowserStorages): BrowserTokens {
  /** Признак последнего стирания. Разбор — у `lostGuest`. */
  let guestLost = false

  const store: TokenStore = {
    get() {
      try {
        return storages.local().getItem(TOKEN_KEY) ?? storages.session().getItem(GUEST_KEY)
      } catch {
        return null
      }
    },
    set(token) {
      try {
        if (token == null) {
          // Гостем был токен из вкладки, если постоянного не было: чтение
          // идёт в том же порядке, что `get`.
          guestLost = storages.local().getItem(TOKEN_KEY) == null && storages.session().getItem(GUEST_KEY) != null
          storages.local().removeItem(TOKEN_KEY)
          storages.session().removeItem(GUEST_KEY)
          return
        }
        // Сюда приходят токены ПРИЗНАННЫХ сессий — после почты или пароля.
        // Гостевой кладётся отдельной дорогой (`keepAsGuest`), и снятие
        // гостевого ключа здесь и есть превращение гостя в аккаунт.
        storages.local().setItem(TOKEN_KEY, token)
        storages.session().removeItem(GUEST_KEY)
      } catch {
        /* приватный режим — живём без запоминания */
      }
    },
  }

  return {
    store,
    /**
     * Отдельной функцией, а не флагом внутри `set`: скрытое состояние в
     * хранилище токенов — это ровно то место, где потом ищут причину «почему
     * человек разлогинился» неделю.
     */
    keepAsGuest(token) {
      try {
        storages.session().setItem(GUEST_KEY, token)
        storages.local().removeItem(TOKEN_KEY)
      } catch {
        /* приватный режим: гость проживёт ровно эту загрузку страницы */
      }
    },
    keepSignedIn,
    // Состояние лестницы регистрации сервер отдаёт ТОЛЬКО гостю (`stateOf`
    // возвращает null всем, у кого есть номер или подтверждённая почта), и тот
    // же признак считает гостем сессия (`guest` в session.tsx).
    settle(me) {
      if (me.signup == null) keepSignedIn()
    },
    lostGuest: () => guestLost,
  }

  function keepSignedIn(): void {
    try {
      const guest = storages.session().getItem(GUEST_KEY)
      if (guest == null || storages.local().getItem(TOKEN_KEY) != null) return
      storages.local().setItem(TOKEN_KEY, guest)
      storages.session().removeItem(GUEST_KEY)
    } catch {
      /* приватный режим: аккаунт проживёт эту вкладку, войти снова можно по почте */
    }
  }
}
